import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import { Box, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { getOptimizedImageUrl, trackClick } from "@/components/_helpers";
import { ARCADE_GAMES } from "@/utils/arcade/games";
import { arcadePath, releaseYear, type ArcadeSong } from "@/utils/arcade/lineup";
import { RELEASE_KIND_LABELS } from "@/utils/musicReleaseTabs";
import { sharedProps } from "@/utils/sharedElements";
import GameThumbnail from "./GameThumbnail";
import { useSongTheme } from "./useSongTheme";

interface ArcadeSongCardProps {
  song: ArcadeSong;
  clickTarget: string;
  eager?: boolean;
}

export function badgeFor(song: ArcadeSong): string {
  if (song.section === "popular") return song.rank === 1 ? "Most played" : `Popular #${song.rank}`;
  if (song.section === "newest") return "New";
  return RELEASE_KIND_LABELS[song.release.kind].one;
}

export function ArcadeSongCard({ song, clickTarget, eager = false }: ArcadeSongCardProps) {
  const theme = useSongTheme(song);
  const palette = theme?.palette;
  const featured = theme ? ARCADE_GAMES[theme.featuredGame] : null;
  const year = releaseYear(song);

  return (
    <Box
      component={RouterLink}
      to={arcadePath(song.slug)}
      viewTransition
      onClick={() => trackClick(clickTarget, { label: `Arcade ${song.title}`, section: "arcade", track: song.title, href: arcadePath(song.slug) })}
      aria-label={`Play ${featured?.name ?? "a game"} with ${song.title}`}
      className="ArcadeSongCard"
      sx={{
        display: "grid",
        gap: "12px",
        p: "10px",
        borderRadius: "18px",
        color: "var(--logoColor2)",
        textDecoration: "none",
        backgroundColor: "color-mix(in srgb, var(--color1) 38%, var(--backgroundColor))",
        border: `1px solid color-mix(in srgb, ${palette?.primary ?? "var(--logoColor)"} 22%, transparent)`,
        transition: "transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease",
        minWidth: 0,
        "&:hover, &:focus-visible": {
          transform: "translateY(-3px)",
          borderColor: palette?.primary ?? "var(--logoColor)",
          boxShadow: `0 18px 40px -24px ${palette?.primary ?? "rgba(0,0,0,0.6)"}`,
          color: "var(--logoColor2)",
        },
        "&:hover .ArcadeSongCard-art > div > img, &:focus-visible .ArcadeSongCard-art > div > img": { transform: "scale(1.05)" },
        "&:hover .ArcadeSongCard-play, &:focus-visible .ArcadeSongCard-play": { transform: "translateY(-2px)" },
        "&:focus-visible": { outline: `2px solid ${palette?.accent ?? "var(--logoColor)"}`, outlineOffset: "3px" },
        "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:hover, &:focus-visible": { transform: "none" } },
      }}
    >
      <Box
        className="ArcadeSongCard-art"
        {...sharedProps("stage", song.slug)}
        sx={{
          position: "relative",
          aspectRatio: "1 / 1",
          borderRadius: "12px",
          overflow: "hidden",
          backgroundColor: palette?.background ?? "var(--color5)",
          "& > div > img": { transition: "transform 0.5s ease" },
        }}
      >
        {theme ? (
          <GameThumbnail kind={theme.featuredGame} theme={theme} coverUrl={song.artwork} width={260} height={260} eager={eager} />
        ) : null}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            background: palette
              ? `linear-gradient(180deg, transparent 50%, color-mix(in srgb, ${palette.backgroundDeep} 85%, transparent) 100%)`
              : "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.75))",
          }}
        />
        {song.artwork ? (
          <Box
            component="img"
            {...sharedProps("art", song.slug)}
            src={getOptimizedImageUrl(song.artwork, { width: 120, height: 120, fit: "cover", quality: 78 })}
            alt=""
            loading={eager ? "eager" : "lazy"}
            decoding="async"
            sx={{
              position: "absolute",
              right: "10px",
              bottom: "10px",
              width: { xs: "34px", sm: "40px" },
              height: { xs: "34px", sm: "40px" },
              borderRadius: "8px",
              objectFit: "cover",
              border: "2px solid rgba(255,255,255,0.85)",
              boxShadow: "0 6px 16px rgba(0,0,0,0.5)",
            }}
          />
        ) : null}
        <Box
          sx={{
            position: "absolute",
            top: "10px",
            left: "10px",
            px: "10px",
            py: "4px",
            borderRadius: "999px",
            fontFamily: "Poppins, sans-serif",
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.04em",
            color: palette?.backgroundDeep ?? "#000",
            backgroundColor: palette?.primary ?? "var(--logoColor)",
          }}
        >
          {badgeFor(song)}
        </Box>
        <Box
          className="ArcadeSongCard-play"
          sx={{
            position: "absolute",
            left: "10px",
            bottom: "10px",
            maxWidth: "calc(100% - 64px)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            minHeight: "32px",
            px: { xs: "9px", sm: "12px" },
            borderRadius: "999px",
            fontFamily: "Poppins, sans-serif",
            fontSize: { xs: "12px", sm: "13px" },
            fontWeight: 700,
            color: palette?.text ?? "#fff",
            backgroundColor: "rgba(0,0,0,0.45)",
            backdropFilter: "blur(6px)",
            transition: "transform 0.25s ease",
            "& svg": { fontSize: "18px", flexShrink: 0 },
          }}
        >
          <SportsEsportsIcon aria-hidden />
          {featured?.name ?? "Play"}
        </Box>
      </Box>
      <Box sx={{ px: "4px", pb: "4px", minWidth: 0 }}>
        <Typography
          component="h3"
          {...sharedProps("title", song.slug)}
          sx={{
            m: 0,
            width: "fit-content",
            maxWidth: "100%",
            fontFamily: "Poppins, sans-serif",
            fontWeight: 600,
            fontSize: { xs: "16px", md: "clamp(15px, 1.05rem, 18px)" },
            lineHeight: 1.3,
            color: "var(--logoColor2)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {song.title}
        </Typography>
        <Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: "13px", color: "var(--logoColor)", opacity: 0.85, lineHeight: 1.4 }}>
          {[RELEASE_KIND_LABELS[song.release.kind].one, year].filter(Boolean).join(" · ")}
        </Typography>
      </Box>
    </Box>
  );
}

export default ArcadeSongCard;
